import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import SiteHealthCard from './components/SiteHealthCard';
import QuickActionCard from './components/QuickActionCard';
import ActivityFeedItem from './components/ActivityFeedItem';
import PerformanceMetricCard from './components/PerformanceMetricCard';
import ActiveCollaboratorsWidget from './components/ActiveCollaboratorsWidget';
import AnalyticsOverviewChart from './components/AnalyticsOverviewChart';
import RecentContentWidget from './components/RecentContentWidget';
import NotificationCenter from './components/NotificationCenter';
import { pagesService, auditService, usersService, analyticsService } from '../../lib/services';
import { useAuth } from '../../contexts/AuthContext';

const DashboardHome = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [pageStats, setPageStats] = useState({ total: 0, published: 0, draft: 0, archived: 0 });
  const [recentPages, setRecentPages] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const { userProfile } = useAuth();

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard-home', icon: 'LayoutDashboard' }
  ];

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoadingStats(true);
      try {
        const [statsResult, pagesResult, auditResult, usersResult, analyticsResult] = await Promise.all([
          pagesService?.getStats(),
          pagesService?.getAll({ limit: 5 }),
          auditService?.getAll({ limit: 20 }),
          usersService?.getAll(),
          analyticsService?.getMetrics(7)
        ]);

        if (!statsResult?.error) setPageStats(statsResult?.data);
        if (!pagesResult?.error) setRecentPages(pagesResult?.data?.slice(0, 5) || []);
        if (!auditResult?.error) {
          setAuditLogs(auditResult?.data || []);
          // Build notifications from audit logs
          const notifTypes = { create: 'success', update: 'info', delete: 'warning', login: 'info', publish: 'success' };
          const notifIcons = { create: 'Created', update: 'Updated', delete: 'Deleted', login: 'Logged in', publish: 'Published' };
          const built = (auditResult?.data || [])?.slice(0, 10)?.map(log => {
            const action = log?.action?.toLowerCase() || 'updated';
            const type = notifTypes?.[action] || 'info';
            const verb = notifIcons?.[action] || log?.action || 'Updated';
            const entity = log?.entityType ? log?.entityType?.replace(/_/g, ' ') : 'item';
            const userName = log?.user?.fullName || log?.user?.email?.split('@')?.[0] || 'System';
            const timeAgo = log?.createdAt ? getTimeAgo(new Date(log.createdAt)) : 'recently';
            return {
              type,
              title: `${verb} ${entity}`,
              message: `${userName} ${verb?.toLowerCase()} a ${entity}`,
              time: timeAgo,
              read: false
            };
          });
          setNotifications(built?.length > 0 ? built : [
            { type: 'info', title: 'No recent activity', message: 'Activity will appear here as your team works', time: 'now', read: true }
          ]);
        }
        if (!usersResult?.error) setActiveUsers(usersResult?.data?.slice(0, 4) || []);

        // Build 7-day analytics chart data
        if (!analyticsResult?.error && analyticsResult?.data?.length > 0) {
          const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const buckets = {};
          // Initialize last 7 days
          for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d?.setDate(d?.getDate() - i);
            const key = d?.toISOString()?.slice(0, 10);
            buckets[key] = { date: days?.[d?.getDay()], visitors: 0, pageViews: 0, _sessions: new Set() };
          }
          analyticsResult?.data?.forEach(event => {
            const key = event?.createdAt ? new Date(event.createdAt)?.toISOString()?.slice(0, 10) : null;
            if (key && buckets?.[key]) {
              buckets[key].pageViews += 1;
              if (event?.sessionId) buckets?.[key]?._sessions?.add(event?.sessionId);
            }
          });
          const chartData = Object.values(buckets)?.map(b => ({
            date: b?.date,
            visitors: b?._sessions?.size || b?.pageViews,
            pageViews: b?.pageViews
          }));
          setAnalyticsData(chartData);
        } else {
          // Fallback: empty chart with day labels
          const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const fallback = [];
          for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d?.setDate(d?.getDate() - i);
            fallback?.push({ date: days?.[d?.getDay()], visitors: 0, pageViews: 0 });
          }
          setAnalyticsData(fallback);
        }
      } catch (err) {
        console.error('Dashboard data load error:', err);
      } finally {
        setLoadingStats(false);
      }
    };

    loadDashboardData();
  }, []);

  const getTimeAgo = (date) => {
    const diff = Date.now() - date?.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const siteHealthData = {
    status: 'excellent',
    uptime: 99.9,
    lastChecked: '2 minutes ago',
    securityScore: 95
  };

  const quickActions = {
    title: 'Quick Actions',
    description: 'Common tasks and shortcuts',
    actions: [
      { label: 'Create New Page', icon: 'FilePlus', path: '/content-editor', variant: 'default' },
      { label: 'Edit Content', icon: 'FileEdit', path: '/content-editor', variant: 'outline' },
      { label: 'Manage Pages', icon: 'Files', path: '/page-manager', variant: 'outline' },
      { label: 'SEO Optimization', icon: 'TrendingUp', path: '/seo-studio', variant: 'outline' }
    ]
  };

  const performanceMetrics = [
    {
      title: 'Total Pages',
      value: loadingStats ? '...' : String(pageStats?.total),
      change: '+' + pageStats?.published,
      changeType: 'positive',
      icon: 'Files',
      trend: 'published pages'
    },
    {
      title: 'Published',
      value: loadingStats ? '...' : String(pageStats?.published),
      change: pageStats?.total > 0 ? `${Math.round((pageStats?.published / pageStats?.total) * 100)}%` : '0%',
      changeType: 'positive',
      icon: 'CheckCircle',
      trend: 'of total pages'
    },
    {
      title: 'Drafts',
      value: loadingStats ? '...' : String(pageStats?.draft),
      change: pageStats?.draft > 0 ? 'In progress' : 'None',
      changeType: 'neutral',
      icon: 'FileEdit',
      trend: 'pending review'
    },
    {
      title: 'Team Members',
      value: loadingStats ? '...' : String(activeUsers?.length),
      change: 'Active',
      changeType: 'positive',
      icon: 'Users',
      trend: 'in your team'
    }
  ];

  // Map recent pages to content widget format
  const recentContent = recentPages?.map(page => ({
    title: page?.title || 'Untitled',
    status: page?.status || 'draft',
    lastModified: page?.updatedAt ? new Date(page.updatedAt)?.toLocaleDateString() : 'Unknown',
    author: page?.author?.fullName || 'Unknown',
    views: null
  }));

  // Map audit logs to activity feed format
  const recentActivities = auditLogs?.slice(0, 5)?.map(log => ({
    type: log?.entityType || 'content',
    title: `${log?.action || 'Updated'} ${log?.entityType || 'item'}`,
    description: log?.newValues ? JSON.stringify(log?.newValues)?.slice(0, 80) : 'No details',
    timestamp: log?.createdAt ? new Date(log.createdAt) : new Date(),
    user: {
      name: log?.user?.fullName || 'System',
      avatar: null,
      avatarAlt: `Avatar for ${log?.user?.fullName || 'System'}`
    }
  }));

  // Map users to collaborators format
  const activeCollaborators = activeUsers?.map(u => ({
    name: u?.fullName || u?.email?.split('@')?.[0] || 'User',
    avatar: u?.avatarUrl || null,
    avatarAlt: `Avatar for ${u?.fullName || 'User'}`,
    activity: `Role: ${(u?.role || 'editor')?.replace('_', ' ')}`,
    activityIcon: u?.role === 'super_admin' ? 'ShieldCheck' : u?.role === 'admin' ? 'Shield' : 'Edit3'
  }));

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={setIsSidebarCollapsed} />
      <Header 
        isCollapsed={isSidebarCollapsed} 
        userName={userProfile?.full_name || userProfile?.email?.split('@')?.[0] || 'Admin'}
        userRole={userProfile?.role || 'user'}
      />
      <main className={`main-content with-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="p-4 md:p-6 lg:p-8">
          <div className="mb-6 md:mb-8">
            <Breadcrumbs items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mt-4">Dashboard</h1>
            <p className="text-sm md:text-base text-muted-foreground mt-2">
              Welcome back, {userProfile?.full_name || userProfile?.email?.split('@')?.[0] || 'Admin'}! Here's what's happening with your website today.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <SiteHealthCard {...siteHealthData} />
            </div>
            <div>
              <QuickActionCard {...quickActions} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {performanceMetrics?.map((metric, index) => (
              <PerformanceMetricCard key={index} {...metric} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <AnalyticsOverviewChart
                data={analyticsData}
                title="Traffic Overview"
                description="Last 7 days performance"
              />
            </div>
            <div>
              <ActiveCollaboratorsWidget collaborators={activeCollaborators} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Recent Activity</h3>
                  <p className="text-xs md:text-sm text-muted-foreground">Latest updates and changes</p>
                </div>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {recentActivities?.length > 0 ? (
                  recentActivities?.map((activity, index) => (
                    <ActivityFeedItem key={index} activity={activity} />
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
                )}
              </div>
            </div>

            <div>
              <RecentContentWidget contentItems={recentContent} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <NotificationCenter notifications={notifications} />

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Quick Tips</h3>
                  <p className="text-xs md:text-sm text-muted-foreground">Helpful suggestions to improve your site</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <h4 className="text-sm font-medium text-foreground mb-2">Optimize Your Images</h4>
                  <p className="text-xs text-muted-foreground mb-3">Compress images to improve page load speed by up to 40%</p>
                  <button className="text-xs text-primary hover:underline font-medium">Learn More →</button>
                </div>
                <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
                  <h4 className="text-sm font-medium text-foreground mb-2">Improve SEO Score</h4>
                  <p className="text-xs text-muted-foreground mb-3">Add meta descriptions to pages missing them</p>
                  <button className="text-xs text-success hover:underline font-medium">Fix Now →</button>
                </div>
                <div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
                  <h4 className="text-sm font-medium text-foreground mb-2">Publish Draft Pages</h4>
                  <p className="text-xs text-muted-foreground mb-3">{pageStats?.draft} pages are in draft - review and publish them</p>
                  <button className="text-xs text-warning hover:underline font-medium">Review Content →</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardHome;